import type { Logger } from "@expert/logging";
import type { GlobalConfig } from "@simplrsupport/headless-chat-web";
import { setGlobalConfig } from "@simplrsupport/headless-chat-web";
import { useReactAnalytics } from "@soluto-private/eventualize-react";
import { useEffect, useMemo } from "react";
import { startSimplrSession } from "./sessionHandling";

interface SimplrInitializersProps {
    globalConfig?: Pick<GlobalConfig, "customFlowSystemId" | "publicServerDomain" | "widgetId">;
    logger: Logger;
    isSimplrGuideEnabled: boolean;
    sessionId: string;
}

export function useSimplrInitializers({
    globalConfig,
    isSimplrGuideEnabled,
    logger: loggerProp,
    sessionId,
}: SimplrInitializersProps) {
    const { dispatcher } = useReactAnalytics();

    const { customFlowSystemId, publicServerDomain } = globalConfig || {};
    const widgetId = "asurion_demo_expert_workspace_dev";
    const logger = useMemo(
        () =>
            loggerProp.child({
                customFlowSystemId,
                module: "useSimplrInitializers",
                publicServerDomain,
                widgetId,
            }),
        [customFlowSystemId, loggerProp, publicServerDomain, widgetId],
    );

    // biome-ignore lint/correctness/useExhaustiveDependencies: fix later
    useEffect(() => {
        if (!isSimplrGuideEnabled) return;
        if (!publicServerDomain || !widgetId) {
            logger.warn("Solve Guide | Missing globalConfig values...");
            return;
        }

        logger.info("Solve Guide | Setting Simplr Global Config");
        setGlobalConfig({
            publicServerDomain,
            widgetId,
            publicServerPrefix: "/chat-api",
            handleError: (error) => {
                logger.error({ error }, "Solve Guide | Received error from Simplr logic builder");
                void dispatcher.dispatchBusinessEvent(`Guide_SimplrErrorState`, {
                    customFlowSystemId,
                    globalConfig,
                    sessionId,
                });
            },
            analytics: {
                trackEvent: console.log,
            },
            calculateProgress: true,
            customFlowSystemId,
        });

        logger.info("Solve Guide | Calling start session with Simplr immediately on session start");
        startSimplrSession({ metadata: {}, sessionId });
    }, [isSimplrGuideEnabled, sessionId]);
}
